import React, { useEffect, useState } from "react"
import Input from "../../../components/Input/Input"
import Button from "../../../components/Button/Button"
import Calendar from "../../../components/Calendar/Calendar"
import { CrossCloseIcon } from "../../../components/Icons/Icons"
import { getCreditReportsStatuses, getCreditReportsTypes } from "../../../features/creditReports/creditReports"
import { useMyContextLoading } from "../../../components/LoadingContextProvider/LoadingContextProvider"
import { useDispatch } from "react-redux"
import Dropdown from "../../../components/Dropdown/Dropdown"
import usePinnedFeatureFlag from "../../../hooks/usePinnedFeatureFlag"
import { DEBT_AMOUNT_DECIMALS } from "../../../config/featureFlagKeys"
import { sanitizeMoneyInput } from "../../../utils/amount"
import { isRequiredField } from "./CreditSelect/helper"

const DynamicExperianFields = ({
  fields,
  setFields,
  initialFieldSet,
  setErrors,
  errors,
  isUpdate,
  showFieldErrors = false,
  revealErrors = false,
  rules = null
}) => {
  const { triggerLoading } = useMyContextLoading()
  const dispatch = useDispatch()
  const { enabled: amountDecimals } = usePinnedFeatureFlag(DEBT_AMOUNT_DECIMALS)
  const amountValue = (value) => (amountDecimals ? sanitizeMoneyInput(value) : value?.replace(/[.,]/g, ''))
  const [creditReportTypes, setCreditReportTypes] = useState([])
  const [creditReportStatuses, setCreditReportStatuses] = useState([])
  const [selectedType, setSelectedType] = useState()
  const [selectedStatus, setSelectedStatus] = useState()

  const fieldError = (index, field) => {
    if (!showFieldErrors || !revealErrors) return undefined
    const message = errors?.[index]?.[field]
    return typeof message === "string" && message.length > 0 ? message : undefined
  }

  const fieldLabel = (field, label) => (rules && isRequiredField(rules, field) ? `${label} *` : label)

  const handleAddFields = () => {
    setFields([...fields, { ...initialFieldSet }])
  }

  const handleRemoveFields = (index) => {
    const updatedFields = fields?.filter((_, i) => i !== index)
    setFields(updatedFields)
  }

  const handleFieldChange = (index, field, value) => {
    const updatedFields = [...fields]
    updatedFields[index][field] = value
    setFields(updatedFields)

    if (value && Array.isArray(errors) && Array.isArray(errors).length > 0) {
      const updatedErrors = [...errors]
      updatedErrors[index] = { ...updatedErrors[index], [field]: false }
      setErrors(updatedErrors)
    }
  }

  const getCreditReport = async () => {
    try {
      triggerLoading(true)
      const responseTypes = await dispatch(getCreditReportsTypes())
      const types = responseTypes?.payload?.data
      if (types) {
        const formattedData = types?.map(item => ({
          value: item._id,
          label: item.description
        }))
        setSelectedType(formattedData?.find(type => type?.value === fields[0]?.accountType?._id)?.value)
        setCreditReportTypes(formattedData)
      }
      const responseStatuses = await dispatch(getCreditReportsStatuses())
      if (responseStatuses?.payload?.data) {
        const formattedData = responseStatuses.payload.data?.map(item => ({
          value: item._id,
          label: item.description
        }))
        setSelectedStatus(formattedData?.find(status => status?.value === fields[0]?.accountStatus?._id)?.value)
        setCreditReportStatuses(formattedData)
      }

    } catch (error) {
      console.error("Error fetching credit reports:", error)
    } finally {
      triggerLoading(false)
    }
  }

  const handleAccountNumberBlur = (index, value) => {
    const length = value.length

    setErrors((prev) => {
      const updated = [...prev]
      if (length < 8 || length > 17) {
        updated[index] = {
          ...updated[index],
          accountNumber: "Account number should be 8 to 17 characters long",
        }
      } else {
        updated[index] = {
          ...updated[index],
          accountNumber: null,
        }
      }
      return updated
    })
  }

  useEffect(() => {
    getCreditReport()
  }, [])

  return (
    <div className="max-w-[1000px] flex flex-col ">
      {fields?.map((field, index) => (
        <div key={index} className="flex flex-col mb-8 ">
          <div data-testid="experian-debt-row" className="flex border border-divider rounded-lg p-5 shadow-card dark:shadow-none justify-start items-start">
            <div className="flex gap-4 flex-wrap justify-between">
              <Dropdown
                height="48px"
                heightPaddings={{ paddingBottom: "2px" }}
                label={fieldLabel("accountType", "Account Type")}
                value={selectedType}
                className="w-[282px]"
                menuItems={creditReportTypes}
                errorMsg={fieldError(index, "accountType")}
                setValue={(e) => {
                  setSelectedType(e)
                  handleFieldChange(index, "accountType", e)
                }
                }
                placeholder="Select Account Type"
              />
              <Input
                type="text"
                parentClassName="w-[282px]"
                classes="h-12"
                width="w-full"
                minLength={8}
                maxLength={17}
                label={fieldLabel("accountNumber", "Account Number")}
                placeholder="Enter account number"
                value={field?.accountNumber}
                errorMsg={fieldError(index, "accountNumber")}
                onChange={(e) =>
                  handleFieldChange(index, "accountNumber", e.target.value.replace(/[^a-zA-Z0-9]/g, ''))
                }
                onBlur={(e) => handleAccountNumberBlur(index, e.target.value)}
              />
              <Calendar
                height="42px"
                parentClassName="w-[282px]"
                calendarWidth="282px"
                label={fieldLabel("openedDate", "Opened Date")}
                placeholder="MM-DD-YYYY"
                value={field.openedDate}
                errorMsg={fieldError(index, "openedDate")}
                onChange={(date) => handleFieldChange(index, "openedDate", date)}
              />
              <Calendar
                height="42px"
                parentClassName="w-[282px]"
                calendarWidth="282px"
                label={fieldLabel("reportedDate", "Reported Date")}
                placeholder="MM-DD-YYYY"
                value={field.reportedDate}
                errorMsg={fieldError(index, "reportedDate")}
                onChange={(date) => handleFieldChange(index, "reportedDate", date)}
              />
              <Calendar
                height="42px"
                parentClassName="w-[282px]"
                calendarWidth="282px"
                label={fieldLabel("closedDate", "Closed Date")}
                placeholder="MM-DD-YYYY"
                value={field.closedDate}
                errorMsg={fieldError(index, "closedDate")}
                onChange={(date) => handleFieldChange(index, "closedDate", date)}
              />
              <Calendar
                height="42px"
                parentClassName="w-[282px]"
                label={fieldLabel("lastPaymentDate", "Last Payment Date")}
                calendarWidth="282px"
                placeholder="MM-DD-YYYY"
                value={field.lastPaymentDate}
                errorMsg={fieldError(index, "lastPaymentDate")}
                onChange={(date) => handleFieldChange(index, "lastPaymentDate", date)}
              />
              <Dropdown
                height="48px"
                heightPaddings={{ paddingBottom: "2px" }}
                label={fieldLabel("isOpenAccount", "Account Open or Closed")}
                value={field.isOpenAccount}
                className="w-[282px]"
                menuItems={[{ label: "Open", value: true }, { label: "Closed", value: false }]}
                errorMsg={fieldError(index, "isOpenAccount")}
                setValue={(value) => handleFieldChange(index, "isOpenAccount", value)}
                placeholder="Select Status"
              />
              <Dropdown
                height="48px"
                heightPaddings={{ paddingBottom: "2px" }}
                label={fieldLabel("accountStatus", "Account Status")}
                value={selectedStatus}
                className="w-[282px]"
                menuItems={creditReportStatuses}
                errorMsg={fieldError(index, "accountStatus")}
                setValue={(e) => {
                  setSelectedStatus(e)
                  handleFieldChange(index, "accountStatus", e)
                }}
                placeholder="Select Status"
              />
              <Input
                parentClassName="w-[282px]"
                classes="h-12"
                width={"w-full"}
                label={fieldLabel("creditorName", "Creditor Name")}
                placeholder="Bank Of America"
                value={field.creditorName}
                errorMsg={fieldError(index, "creditorName")}
                onChange={(e) => handleFieldChange(index, "creditorName", e.target.value)}
              />
              <Input
                type="number"
                step={amountDecimals ? "any" : undefined}
                parentClassName="w-[282px]"
                classes="h-12"
                width={"w-full"}
                label={fieldLabel("balanceAmount", "Balance Owed")}
                placeholder="0.00"
                value={field.balanceAmount}
                errorMsg={fieldError(index, "balanceAmount")}
                onChange={(e) => handleFieldChange(index, "balanceAmount", amountValue(e.target.value))}
              />
              <Input
                type="number"
                step={amountDecimals ? "any" : undefined}
                parentClassName="w-[282px]"
                classes="h-12"
                width={"w-full"}
                label={fieldLabel("pastDueAmount", "Past Due Amount")}
                placeholder="0.00"
                value={field.pastDueAmount}
                errorMsg={fieldError(index, "pastDueAmount")}
                onChange={(e) => handleFieldChange(index, "pastDueAmount", amountValue(e.target.value))}
              />
              <Input
                type="number"
                step={amountDecimals ? "any" : undefined}
                parentClassName="w-[282px]"
                classes="h-12"
                width={"w-full"}
                label={fieldLabel("creditLimit", "Credit Limit")}
                placeholder="0.00"
                value={field.creditLimit}
                errorMsg={fieldError(index, "creditLimit")}
                onChange={(e) => handleFieldChange(index, "creditLimit", amountValue(e.target.value))}
              />
            </div>

            {!isUpdate && <button className="" onClick={() => handleRemoveFields(index)}>
              <CrossCloseIcon fill="var(--color-gray-600)" />
            </button>}
          </div>
        </div>
      ))}
      {!isUpdate &&
        <Button onClick={handleAddFields} type="text" styles={{ marginRight: '-14px' }}>+Add New Debt</Button>}
    </div>
  )
}

export default DynamicExperianFields
